(() => {
  if (window.__stwrapCollectionFilters) return;
  window.__stwrapCollectionFilters = true;
  const instances = new WeakMap();
  const read = key => { try { return JSON.parse(sessionStorage.getItem(key)); } catch { return null; } };
  const write = (key, value) => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {} };

  class CollectionFilters {
    constructor(root) {
      this.root = root;
      this.life = new AbortController();
      this.media = matchMedia('(min-width: 1024px)');
      this.key = `stwrap-filters:${root.dataset.collectionUrl}`;
      this.saved = read(this.key) || { groups: {}, expanded: {} };
      this.sequence = 0;
      this.desktopOpen = true;
      this.dialog = root.querySelector('[data-stwf-dialog]');
      const options = { signal: this.life.signal };
      root.addEventListener('change', event => this.change(event), options);
      root.addEventListener('click', event => this.click(event), options);
      root.addEventListener('input', event => {
        if (event.target.matches('[data-stwf-search]')) this.limit(event.target.closest('[data-stwf-group]'));
      }, options);
      root.addEventListener('submit', event => {
        if (event.target.matches('[data-stwf-form]')) event.preventDefault();
      }, options);
      root.addEventListener('toggle', event => {
        if (!event.target.matches('details[data-stwf-group]')) return;
        const group = event.target;
        group.querySelector('summary').setAttribute('aria-expanded', String(group.open));
        this.saved.groups[group.dataset.param] = group.open;
        write(this.key, this.saved);
        requestAnimationFrame(() => this.hint());
      }, { ...options, capture: true });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeDrawer(); }, options);
      this.dialog.addEventListener('keydown', event => this.trapFocus(event), options);
      this.media.addEventListener('change', () => this.mount(), options);
      window.addEventListener('popstate', () => this.navigate(location.href, { pop: true }), options);
      window.addEventListener('resize', () => this.scheduleHeader(), { ...options, passive: true });
      window.addEventListener('scroll', () => this.scheduleHeader(), { ...options, passive: true });
      this.header = document.querySelector('.header--root');
      this.headerObserver = new ResizeObserver(() => this.scheduleHeader());
      if (this.header) this.headerObserver.observe(this.header);
      this.prepare();
      this.headerOffset();
      history.replaceState({ ...history.state, stwrapFilters: this.capture() }, '', location.href);
    }

    capture() {
      return { y: scrollY, railScroll: this.root.querySelector('[data-stwf-scroll]')?.scrollTop || 0, desktopOpen: this.desktopOpen };
    }

    scheduleHeader() {
      cancelAnimationFrame(this.headerFrame);
      this.headerFrame = requestAnimationFrame(() => this.headerOffset());
    }

    headerOffset() {
      const header = this.header?.getBoundingClientRect();
      const fallback = parseFloat(getComputedStyle(this.root).getPropertyValue('--sticky-offset')) || 0;
      const offset = header ? Math.max(0, header.bottom) : fallback;
      this.root.style.setProperty('--stwf-offset', `${Math.ceil(offset)}px`);
      this.hint();
    }

    limit(group) {
      const search = group.querySelector('[data-stwf-search]');
      const query = (search?.value || '').trim().toLocaleLowerCase();
      const more = group.querySelector('[data-stwf-more]');
      const expanded = !!this.saved.expanded[group.dataset.param];
      let matches = 0;
      group.querySelectorAll('[data-stwf-value]').forEach((row, index) => {
        const match = !query || row.dataset.label.toLocaleLowerCase().includes(query);
        if (match) matches++;
        row.hidden = !match;
        row.classList.toggle('stwf-overflow', !query && !expanded && index >= 6 && !row.querySelector('input').checked);
      });
      if (more) {
        more.hidden = !!query;
        more.setAttribute('aria-expanded', String(expanded));
        more.textContent = expanded ? this.root.dataset.showLess : this.root.dataset.showAll.replace('__COUNT__', more.dataset.count);
      }
      const empty = group.querySelector('[data-stwf-no-values]');
      if (empty) empty.hidden = matches > 0;
      this.hint();
    }

    prepare(state, searches = {}) {
      const panel = this.root.querySelector('[data-stwf-panel]');
      const priorities = this.root.dataset.openGroups.split(',').map(key => key.trim());
      const groups = [...panel.querySelectorAll('[data-stwf-group]')];
      const rank = group => { const n = priorities.indexOf(group.dataset.key); return n < 0 ? 100 : n; };
      groups.sort((a, b) => rank(a) - rank(b)).forEach(group => group.parentElement.append(group));
      groups.forEach(group => {
        const param = group.dataset.param;
        const priority = priorities.indexOf(group.dataset.key);
        group.style.setProperty('--stwf-order', priority < 0 ? 100 : priority);
        if (group.tagName === 'DETAILS') {
          if (param in this.saved.groups) group.open = this.saved.groups[param];
          group.querySelector('summary').setAttribute('aria-expanded', String(group.open));
        }
        const search = group.querySelector('[data-stwf-search]');
        if (search) search.value = searches[param] || '';
        this.limit(group);
      });
      if (state) this.desktopOpen = state.desktopOpen;
      this.grid(read('stwrap-story-grid-columns') === 5 ? 5 : 4);
      this.recoverChips();
      this.mount();
      const scroll = panel.querySelector('[data-stwf-scroll]');
      scroll.scrollTop = state?.railScroll || 0;
      scroll.addEventListener('scroll', () => this.hint(), { passive: true, signal: this.life.signal });
      this.railObserver?.disconnect();
      this.railObserver = new ResizeObserver(() => this.hint());
      this.railObserver.observe(scroll);
      this.root.querySelectorAll('[data-transition-item]').forEach(e => e.setAttribute('data-transition-active', 'true'));
      requestAnimationFrame(() => this.hint());
    }

    mount() {
      const panel = this.root.querySelector('[data-stwf-panel]');
      const host = this.root.querySelector('[data-stwf-host]');
      if (this.media.matches) {
        if (this.dialog.open) this.dialog.close();
        document.documentElement.classList.remove('stwf-lock');
        host.append(panel);
      } else this.dialog.append(panel);
      this.root.querySelector('[data-stwrap-filter-layout]').classList.toggle('stwf-rail-hidden', !this.desktopOpen);
      this.root.querySelector('[data-stwf-toggle]').setAttribute('aria-expanded', String(this.media.matches ? this.desktopOpen : this.dialog.open));
      this.scheduleHeader();
    }

    hint() {
      const scroll = this.root.querySelector('[data-stwf-scroll]');
      const hint = this.root.querySelector('[data-stwf-hint]');
      if (scroll && hint) hint.hidden = !(scroll.clientHeight > 0 && scroll.scrollHeight - scroll.clientHeight - scroll.scrollTop > 10);
    }

    openDrawer() {
      this.dialog.showModal();
      document.documentElement.classList.add('stwf-lock');
      this.root.querySelector('[data-stwf-toggle]').setAttribute('aria-expanded', 'true');
      this.dialog.querySelector('[data-stwf-close]').focus();
      this.hint();
    }

    closeDrawer() {
      this.dialog.close();
      document.documentElement.classList.remove('stwf-lock');
      this.root.querySelector('[data-stwf-toggle]').setAttribute('aria-expanded', 'false');
      this.root.querySelector('[data-stwf-toggle]').focus({ preventScroll: true });
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('button, a[href], input, select, summary, [tabindex="0"]')].filter(e => !e.disabled && e.getClientRects().length);
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }

    recoverChips() {
      const count = this.root.querySelector('[data-stwrap-count-meta]')?.dataset.currentProducts;
      if (count !== '0' && !this.root.querySelector('[data-stwf-empty]')) return;
      const list = this.root.querySelector('[data-stwf-chip-list]');
      list.replaceChildren();
      const params = new URL(location.href).searchParams;
      params.forEach((value, name) => {
        if (!name.startsWith('filter.')) return;
        const url = new URL(location.href);
        url.searchParams.delete(name);
        params.getAll(name).filter(v => v !== value).forEach(v => url.searchParams.append(name, v));
        url.searchParams.delete('page');
        const chip = document.createElement('a');
        chip.className = 'stwf-chip';
        chip.dataset.stwfChip = '';
        chip.href = url.href;
        chip.textContent = `${value} ×`;
        chip.setAttribute('aria-label', this.root.dataset.removeLabel.replace('__VALUE__', value));
        list.append(chip);
      });
      this.root.querySelector('[data-stwf-chips]').hidden = !list.childElementCount;
    }

    grid(columns) {
      this.root.querySelector('[data-body-grid]').dataset.columns = columns;
      this.root.querySelectorAll('[data-stwf-columns]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.stwfColumns) === columns)));
    }

    change(event) {
      const input = event.target;
      if (input.matches('[data-stwf-sort]')) {
        const url = new URL(location.href);
        url.searchParams.set('sort_by', input.value);
        url.searchParams.delete('page');
        this.navigate(url);
      } else if (input.matches('[data-stwf-form] [name^="filter."]')) {
        const form = input.closest('form');
        const url = new URL(location.href);
        const names = new Set([...form.querySelectorAll('[name^="filter."]')].map(e => e.name));
        names.forEach(name => url.searchParams.delete(name));
        for (const [name, value] of new FormData(form)) if (String(value).trim()) url.searchParams.append(name, value);
        url.searchParams.delete('page');
        this.navigate(url);
      }
    }

    click(event) {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.matches('[data-stwf-toggle]')) {
        if (this.media.matches) { this.desktopOpen = !this.desktopOpen; this.mount(); }
        else this.openDrawer();
      } else if (target.matches('[data-stwf-close]')) this.closeDrawer();
      else if (target.matches('[data-stwf-more]')) {
        const group = target.closest('[data-stwf-group]');
        this.saved.expanded[group.dataset.param] = !this.saved.expanded[group.dataset.param];
        write(this.key, this.saved);
        this.limit(group);
      } else if (target.matches('[data-stwf-columns]')) {
        const columns = Number(target.dataset.stwfColumns);
        write('stwrap-story-grid-columns', columns);
        this.grid(columns);
      } else if (target.matches('[data-stwf-chip], [data-stwf-clear], [data-body-pagination] a')) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const url = new URL(target.href);
        if (target.matches('[data-stwf-clear]')) {
          const current = new URL(location.href);
          [...current.searchParams.keys()].filter(k => k.startsWith('filter.') || k === 'page').forEach(k => current.searchParams.delete(k));
          url.search = current.search;
        }
        this.navigate(url, { pagination: !!target.closest('[data-body-pagination]') });
      }
    }

    async navigate(input, { pop = false, pagination = false } = {}) {
      const url = new URL(input, location.href);
      url.searchParams.delete('section_id');
      url.hash = '';
      const current = new URL(location.href);
      ['preview_theme_id', 'view'].forEach(key => {
        if (!url.searchParams.has(key) && current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      const before = this.capture();
      if (!pop) history.replaceState({ ...history.state, stwrapFilters: before }, '', location.href);
      const restored = pop ? history.state?.stwrapFilters : before;
      const focus = document.activeElement;
      const focusId = this.root.contains(focus) ? focus.id : '';
      const focusedChip = focus?.matches('[data-stwf-chip], [data-stwf-clear], [data-body-pagination] a');
      const searches = Object.fromEntries([...this.root.querySelectorAll('[data-stwf-search]')].map(e => [e.closest('[data-stwf-group]').dataset.param, e.value]));
      this.pending?.abort();
      const controller = new AbortController();
      this.pending = controller;
      const sequence = ++this.sequence;
      this.root.setAttribute('aria-busy', 'true');
      this.root.querySelector('[data-stwf-error]').hidden = true;
      const sectionUrl = new URL(url);
      sectionUrl.searchParams.set('section_id', this.root.dataset.sectionId);
      try {
        const response = await fetch(sectionUrl, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Collection request failed');
        const html = await response.text();
        if (sequence !== this.sequence) return;
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-stwrap-phase2] .stwc__inner');
        if (!next?.querySelector('[data-body-grid]')) throw new Error('Missing collection section');
        const nextGrid = next.querySelector('[data-body-grid]');
        const count = nextGrid.querySelector('[data-stwrap-count-meta]')?.dataset.currentProducts;
        this.root.querySelector('[data-stwf-panel]')?.remove();
        this.root.querySelector('.stwc__inner').replaceChildren(...next.childNodes);
        if (!pop && url.href !== location.href) history.pushState({ stwrapFilters: before }, '', url.href);
        this.prepare(restored || before, searches);
        if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
        else if (focusedChip) this.root.querySelector('[data-stwf-toggle]').focus({ preventScroll: true });
        if (this.dialog.open && !this.dialog.contains(document.activeElement)) this.dialog.querySelector('[data-stwf-close]').focus({ preventScroll: true });
        if (pop) window.scrollTo({ top: restored?.y ?? before.y, behavior: 'instant' });
        else if (pagination) {
          const toolbar = this.root.querySelector('.stwf-toolbar');
          const offset = parseFloat(this.root.style.getPropertyValue('--stwf-offset')) || 0;
          window.scrollTo({ top: Math.max(0, toolbar.getBoundingClientRect().top + scrollY - offset - 12), behavior: 'instant' });
        } else if (!this.dialog.open) window.scrollTo({ top: before.y, behavior: 'instant' });
        this.root.dispatchEvent(new CustomEvent('stwrap:filters:updated', { bubbles: true, detail: { count: Number(count), url: url.href } }));
        if (typeof window.trigger === 'function') window.trigger(`theme:transition:reload:${this.root.dataset.sectionId}`);
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence) {
          const alert = this.root.querySelector('[data-stwf-error]');
          alert.textContent = this.root.dataset.errorLabel;
          alert.hidden = false;
          if (pop) location.reload();
          else {
            this.root.querySelectorAll('[data-stwf-form] input[type="checkbox"]').forEach(e => { e.checked = current.searchParams.getAll(e.name).includes(e.value); });
            this.root.querySelectorAll('[data-stwf-sort]').forEach(e => { e.value = current.searchParams.get('sort_by') || e.querySelector('option[selected]')?.value || 'manual'; });
          }
        }
      } finally {
        if (sequence === this.sequence) { this.pending = null; this.root.removeAttribute('aria-busy'); }
      }
    }

    destroy() {
      this.pending?.abort();
      this.life.abort();
      this.railObserver?.disconnect();
      this.headerObserver.disconnect();
      cancelAnimationFrame(this.headerFrame);
      if (this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('stwf-lock');
    }
  }

  function init() {
    document.querySelectorAll('[data-stwrap-phase2]').forEach(root => {
      if (!instances.has(root)) instances.set(root, new CollectionFilters(root));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.querySelector('[data-stwrap-phase2]');
    if (root) { instances.get(root)?.destroy(); instances.delete(root); }
  });
})();
